import { ExploreActions } from '@/components/explore/ExploreActions';
import { ExploreNavTabs } from '@/components/explore/ExploreNavTabs';
import { ExploreSearchResults } from '@/components/explore/ExploreSearchResults';
import { ExploreOutletContext } from '@/components/layouts/Layout';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import { SortDirection, SortField } from '@/components/widget/content-sort-select';
import { useFilterDetail, useNavigateWithContentFlush, useUser } from '@/hooks';
import { useSearchStore } from '@/store/searchStore';
import { useSelectedItemStore } from '@/store/selectedItemStore';
import { useViewContextStore } from '@/store/viewContextStore';
import { LANDING_FILTER_STORAGE_KEY } from '@/utils/auth-utils';
import { DEFAULT_SORT } from '@/utils/content-sort-utils';
import { useEffect, useRef, useState } from 'react';
import { Navigate, useOutletContext } from 'react-router-dom';

export default function LayoutExplore() {
  const { setScreen } = useOutletContext<ExploreOutletContext>();
  const navigate = useNavigateWithContentFlush();

  useEffect(() => {
    setScreen(null);
  }, [setScreen]);

  const { data: memberContext, isLoading } = useUser();
  const query = useSearchStore((state) => state.query);
  const activeSavedFilterId = useSearchStore((state) => state.activeSavedFilterId);
  const activeSavedFilterDefinition = useSearchStore((state) => state.activeSavedFilterDefinition);
  const setQuery = useSearchStore((state) => state.setQuery);
  const setActiveSavedFilter = useSearchStore((state) => state.setActiveSavedFilter);
  const resetInputValue = useSearchStore((state) => state.resetInputValue);
  const tab = useSearchStore((state) => state.filters.tab);
  const selectedItems = useSelectedItemStore((state) => state.selectedItems);
  const initializedRef = useRef(false);

  useEffect(() => {
    useViewContextStore.getState().setSlot('explore', {
      ...(tab !== undefined ? { tab } : {}),
      ...(activeSavedFilterId !== null ? { savedFilterId: activeSavedFilterId } : {}),
      query,
      selectedContentIds: selectedItems,
    });
    return () => useViewContextStore.getState().clearSlot('explore');
  }, [tab, activeSavedFilterId, query, selectedItems]);

  // One-shot landing view (preference contract): the key is written by login
  // completion AND member switch. Read purely here — React may discard a render,
  // so the key is cleared by the initialization effect below at the moment it is
  // actually consumed, resolvable or not, and a refresh can never re-trigger it.
  const [landingFilterId] = useState<number | null>(() => {
    const raw = sessionStorage.getItem(LANDING_FILTER_STORAGE_KEY);
    if (raw === null) return null;
    const id = Number(raw);
    return Number.isFinite(id) ? id : null;
  });

  // Resolve the landing view by id rather than peeking at whatever the saved-filter
  // list happened to seed into the cache: the list excludes tabs, and its per-id
  // cache entries have no observer, so they are garbage-collected out from under a
  // peek. This query resolves tabs and saved filters alike.
  const landingFilterQuery = useFilterDetail(landingFilterId ?? 0);

  // Pagination state
  const [cursor, setCursor] = useState<number>(0);
  const [limit] = useState<number>(20); // Page size

  // Sorting state
  const [sortOptions, setSortOptions] = useState<{ field: SortField; direction: SortDirection }>(
    DEFAULT_SORT
  );
  const [sortExplicit, setSortExplicit] = useState(false);
  const handleSortChange = (next: { field: SortField; direction: SortDirection }) => {
    setSortOptions(next);
    setSortExplicit(true);
  };

  // Initialize search store with the one-shot landing view (written at login
  // completion or member switch), else the default tab, on mount (only once)
  useEffect(() => {
    if (isLoading || !memberContext || initializedRef.current) {
      return;
    }
    // No landing stash: an already-determined view wins. A tab carries its own
    // `tab:` token in the query; a saved filter usually has an empty
    // definition.search, so it is only visible as an active saved filter id.
    if (landingFilterId === null && (query.trim() || activeSavedFilterId !== null)) {
      return;
    }
    // A stash is authoritative evidence of a fresh landing (login or member
    // switch): it overrides any query persisted across an SPA logout→login.
    // Wait for the landing filter to resolve (or fail) so the id can be applied.
    if (landingFilterId !== null && landingFilterQuery.isLoading) {
      return;
    }
    initializedRef.current = true;
    sessionStorage.removeItem(LANDING_FILTER_STORAGE_KEY);

    if (landingFilterId !== null) {
      const landingTab = memberContext.accountTabs.find((tab) => tab.id === landingFilterId);
      if (landingTab?.searchFilter) {
        setQuery(landingTab.searchFilter);
        resetInputValue(navigate, undefined, landingTab.searchFilter);
        return;
      }
      // A token-less tab (member filter promoted to tab, no shortName) and a plain
      // saved filter land the same way: apply the filter definition directly.
      const landingFilter = landingFilterQuery.data;
      if (landingFilter) {
        setActiveSavedFilter(landingFilter.id, landingFilter.definition, landingFilter.name);
        const landingSearch = landingFilter.definition.search ?? '';
        setQuery(landingSearch);
        resetInputValue(navigate, undefined, landingSearch);
        return;
      }
      // Unresolvable id: the prompt-1 preference contract says ignore it and
      // fall back to today's behaviour (the member's default tab).
    }

    if (memberContext.defaultTab) {
      const defaultTabSearchFilter = memberContext.defaultTab.searchFilter;
      if (defaultTabSearchFilter) {
        setQuery(defaultTabSearchFilter);
        resetInputValue(navigate, undefined, defaultTabSearchFilter);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, query, memberContext, landingFilterQuery.isLoading, landingFilterQuery.data]); // Only depend on load state and query - use ref to prevent re-initialization

  // Reset cursor when sort or query changes
  useEffect(() => {
    setCursor(0);
  }, [sortOptions, query, activeSavedFilterId]);

  useEffect(() => {
    setSortExplicit(false);
  }, [query]);

  // Keep sorting aligned with the active saved filter definition. When the filter is
  // cleared (a tab click), its sort must go with it: an `importance` sort carried onto
  // a CONVERSATION-projection tab is rejected by the API and renders an empty list.
  useEffect(() => {
    if (!activeSavedFilterDefinition) {
      setSortOptions(DEFAULT_SORT);
      setSortExplicit(false);
      return;
    }
    setSortExplicit(activeSavedFilterDefinition.orderBy !== undefined);
    setSortOptions(
      activeSavedFilterDefinition.orderBy === 'importance'
        ? { field: 'importance', direction: 'asc' }
        : {
            field: activeSavedFilterDefinition.orderBy === 'title' ? 'title' : 'datetime',
            direction: activeSavedFilterDefinition.orderDir ?? 'desc'
          }
    );
  }, [activeSavedFilterDefinition]);

  // Wait for auth to be properly initialized before checking user
  if (isLoading) {
    return <Skeleton className="m-4 flex h-8" />;
  }

  if (!memberContext) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-col">
      <ExploreActions 
        cursor={cursor}
        setCursor={setCursor}
        limit={limit}
        sortOptions={sortOptions}
        setSortOptions={handleSortChange}
        sortExplicit={sortExplicit}
      />
      <ExploreNavTabs />
      <ExploreSearchResults
        cursor={cursor}
        limit={limit}
        sortOptions={sortOptions}
        sortExplicit={sortExplicit}
      />
    </div>
  );
}
